// Models used in the application. Exported as constants for easy management and updates.

// Some models are offered for free through open router but there are a few drawbacks:
// - For the free endpoint, please do not upload any confidential information or personal data (such as voices or faces of people).
//   Your use is logged for security purposes and to improve NVIDIA products and services. The logged session data for improvement
//   purposes is not linked to your identity or any persistent identifier. For more information about NVIDIA's data processing practices,
//   see Privacy Policy(opens in new tab). By using this free endpoint, you consent to NVIDIA's collection, recording, and use of such
//   information and the NVIDIA API Trial Terms of Service.
// - The free endpoint is rate limited and may be unavailable at times. https://openrouter.ai/docs/api-reference/limits
// - The free model is not guarenteed to be available/free in the future.
// - The free model cant process images in general
// - NEVER USE IN PRODUCTION!!!
export const TestModel = 'openai/gpt-4o-mini'; // 'z-ai/glm-5.2:free'; // 'nvidia/nemotron-3.5-lightning:free'; //'nvidia/nemotron-3-ultra-550b-a55b:free';
export const useTestModel = false; // !!!ALWAYS set to FALSE for PRODUCTION!!!

// if points in gs are natural language (one point each) regex is not sufficient for automatic point calculation
export const calcMaxPointsModel = useTestModel ? TestModel : 'google/gemini-3-flash-preview';
export const calcMaxPointsTemperature = 0;

// mainAgent model in generate answer
export const generateAnswerModel = useTestModel ? TestModel : 'openai/gpt-5.4';
export const generateAnswerTemperature = 0.4;

// final stream (responding to toolcalls) in generate answer
export const generateAnswerFinalStreamModel = useTestModel ? TestModel : 'openai/gpt-5.4';
export const generateAnswerFinalStreamTemperature = 0.4;

// prompt suggestions model
export const promptSuggestionsModel = useTestModel ? TestModel : 'openai/gpt-5.4';
export const promptSuggestionsTemperature = 0.4;

// bbx agent model
export const bbxAgentModel = useTestModel ? TestModel : 'google/gemini-3-flash-preview';
export const bbxAgentDefaultTemperature = 0;
export const bbxAgentRetryTemperature = 0.2;

// grammar LLM evaluation agent — context-sensitive alternative to LanguageTool
export const grammarLLMEvaluationModel = useTestModel ? TestModel : 'openai/gpt-5.4';
export const grammarLLMEvaluationTemperature = 0;

// grammar error coordinate agent — locates each grammar error on the student submission image
export const grammarCoordinateAgentModel = useTestModel ? TestModel : 'google/gemini-3-flash-preview';
export const grammarCoordinateAgentDefaultTemperature = 0;
export const grammarCoordinateAgentRetryTemperature = 0.2;

// task extraction confidence model, checks if the extracted task description is legitimate
export const taskExtractionConfidenceModel = useTestModel ? TestModel : 'google/gemini-3-flash-preview';
export const taskExtractionConfidenceTemperature = 0;

// model for default grading, add models for task specific grading later
export const defaultGradingModel = useTestModel ? TestModel : 'anthropic/claude-sonnet-4.6';
export const defaultGradingTemperature = 0;

// model to generate the grading scheme
export const gsGenerationAgentModel = useTestModel ? TestModel : 'openai/gpt-5.4';
export const gsGenerationAgentTemperature = 0.1;
// OpenRouter reasoning effort for GS generation (forwarded via the `reasoning` param). 'low' | 'medium' | 'high'.
export const gsGenerationAgentReasoningEffort: 'low' | 'medium' | 'high' = 'medium';

// model to generate the master solution
export const msGenerationAgentModel = useTestModel ? TestModel : 'openai/gpt-5.4';
export const msGenerationAgentTemperature = 0.1;
// OpenRouter reasoning effort for MS generation (forwarded via the `reasoning` param). 'low' | 'medium' | 'high'.
export const msGenerationAgentReasoningEffort: 'low' | 'medium' | 'high' = 'medium';

// ocr agent model
export const ocrAgentModel = useTestModel ? TestModel : 'openai/gpt-5.4-mini';
export const ocrAgentDefaultTemperature = 0;
export const ocrAgentRetryTemperature = 0.2;

// if pdf is too long, capacity of flash is not sufficient => use pro for long documents
export const shortSplitAgentModel = useTestModel ? TestModel : 'google/gemini-3-flash-preview';
export const shortSplitAgentTemperature = 0;
export const longSplitAgentModel = useTestModel ? TestModel : 'google/gemini-3.1-pro-preview';
export const longSplitAgentTemperature = 0;

// model for extracting the task structre from an exam
export const structureAgentModel = useTestModel ? TestModel : 'openai/gpt-5.4';
export const structureAgentDefaultTemperature = 0;
export const structureAgentRetryTemperature = 0.2;

// model that checks if a task needs an answer or is only there for structure
export const needsAnswerAgentModel = useTestModel ? TestModel : 'google/gemini-3-flash-preview';
export const needsAnswerAgentTemperature = 0;

// class/course metadata agent — splits a free-text class label (e.g. "LK 12 Sozialkunde", "8c Mathe")
// into grade/suffix/subject; short text-only classification, so the cheap flash model is enough.
export const classMetadataAgentModel = useTestModel ? TestModel : 'google/gemini-3-flash-preview';
export const classMetadataAgentTemperature = 0;

// model for evaluating the confidence of a grading
export const evaluateGradingConfidenceModel = useTestModel ? TestModel : 'openai/gpt-4o-mini';
export const evaluateGradingConfidenceTemperature = 0;

// lightweight model that curates/merges teacher memory rules on save_teacher_memory
export const memoryAgentModel = useTestModel ? TestModel : 'google/gemini-3-flash-preview';
export const memoryAgentTemperature = 0;

// translates a natural-language math description (e.g. "integral from 0 to 10 of x^2") into LaTeX
export const nlToLatexModel = useTestModel ? TestModel : 'google/gemini-3-flash-preview';
export const nlToLatexTemperature = 0;

// exam creation — writes the typst source of a new exam (blueprint, then the document itself)
export const examCreationModel = 'openai/gpt-5.4';
export const examCreationTemperature = 0.3;
// OpenRouter reasoning effort for exam creation (forwarded via the `reasoning` param).
export const examCreationReasoningEffort: 'low' | 'medium' | 'high' = 'medium';

// exam creation — repairing a source that failed to compile or failed validation. Deterministic: the
// diagnostics say what is wrong, so there is nothing to be creative about.
export const examRepairTemperature = 0;

// exam creation — generates the figures a created exam needs (diagrams for LABEL_IMAGE, illustrations)
export const examImageModel = 'google/gemini-3.1-flash-lite-image';
// The output modalities to request alongside it — NOT universal, has to match what this specific model
// supports. A pure image-generation endpoint like flux only ever returns 'image'; asking it for 'text' too
// (the shape a multimodal chat-plus-image model such as Gemini's image-preview needs) 404s with "No
// endpoints found that support the requested output modalities". Change this together with the model
// above, never independently of it.
export const examImageModalities: ('image' | 'text')[] = ['image', 'text'];
// Whether this model accepts a native `negative_prompt` field — a real classifier-free-guidance channel
// the model was TRAINED to push away from, distinct from prose in the main prompt that the model merely
// has to interpret (and might over- or under-weight either way — see the STRICTLY FORBIDDEN clauses in
// exam-images.ts, kept for models without this channel). Recraft supports it; flux and chat-completions-
// style image models (Gemini's image-preview) generally do not. Change this together with the model
// above — sending it to a model that ignores or rejects unknown fields risks the same kind of failure the
// modalities mismatch caused.
export const examImageSupportsNegativePrompt = false as boolean;

// exam creation — reviews each generated figure before it is allowed into a layout.
//
// THE CREATOR'S CONTEXT, NOT THE CREATOR'S WEIGHTS. What makes this review the creator's own judgement is
// the turn it is given: its own brief, its own acceptance criteria, and the tasks the figure has to serve
// (see exam-images.ts). A source-text image is SUPPOSED to carry text and a labelling diagram must not —
// telling those apart takes the plan, not a bigger model. So the cheap vision model reads the plan, and
// the judgement is still the creator's: up to three attempts per figure makes this the most-called vision
// step in the system, and the expensive model bought nothing the context does not already give it.
export const examImageReviewModel = 'google/gemini-3-flash-preview';
export const examImageReviewTemperature = 0;

// exam creation — locates the structures a labelling task points at. Mechanical coordinate work against a
// fixed rubric with no exam context in it, so the cheap vision model is the right one.
export const examImageLocateModel = 'google/gemini-3-flash-preview';

// exam creation — writes cetz plotting code for a `plot` material: a coordinate system that already shows
// a function curve or a set of points, as opposed to the blank grid `#grid-paper` draws directly in the
// source (see exam-plots.ts). Correct syntax against one known plotting package is mechanical, not
// judgement — no vision, no review of a rendered image, just typst that either compiles or gets its
// diagnostics fed back — so the cheap model is enough, the same reasoning as the image review models above.
export const examPlotModel = 'google/gemini-3-flash-preview';
export const examPlotTemperature = 0.1;
